import React, { useEffect, useRef } from "react";
import { ErrorBoundary } from "@lululemon/mwa-logger";
import { QVMProvider } from "./quick-view-modal.ctx";
import ModalRenderer from "./ModalRenderer";
import CarouselRenderer from "./CarouselRenderer";
import ContentRenderer from "./ContentRenderer";
import propTypes from "./quick-view-modal.types";
import { useCarouselActions, useQuickViewModal } from "./hooks";

/**
 * This one will open the context for the other modal pieces.
 * @param {*} param0
 * @returns
 */
const QuickViewModal = ({
  data,
  defaultColorCode,
  inventoryData,
  onAddToBag,
  onColorChange,
  onSlideChange,
  onSizeChange,
  onLoad,
  onError,
  ...props
}) => {
  const loaded = useRef(false);
  const {
    selectedColor,
    selectedVariant = {},
    skus = [],
    ...modalData
  } = useQuickViewModal({
    data,
    events: {
      onAddToBag,
      onColorChange,
      onSizeChange,
    },
    defaultColorCode,
    inventoryData,
  });

  const actions = useCarouselActions({
    events: {
      onSlideChange,
    },
    images: selectedVariant?.imageSet?.images,
  });

  /**
   * Used to detect when the modal is officially ready to
   * display the data, we try to pass usefull data to the
   * onLoad event.
   */
  useEffect(() => {
    if (!loaded?.current && !props?.isLoading && data && inventoryData) {
      loaded.current = true;
      onLoad?.({
        product: {
          name:
            data?.productSummary?.displayName || data?.productSummary?.title,
        },
        skus: data?.skus,
        selectedVariant,
        selectedColor,
      });
    }
  }, [
    props?.isLoading,
    selectedVariant,
    data,
    inventoryData,
    modalData,
    selectedColor,
    loaded?.current,
  ]);

  useEffect(() => {
    /**
     * This is to clean up the modal selected size, when the modal
     * got closed the loading status becomes true and we need to
     * make sure that when the guest opens again the modal the
     * new loaded product doesn't have a pre-selected size.
     */
    if (props?.isLoading && modalData?.selectedSizeIdx !== null) {
      modalData?.setSelectedSizeIdx(null, false);
      loaded.current = false;
    }
    /**
     * Same clean up needs to be applied to the color, cannot use
     * the same conditional because the user can open the modal and
     * never select a size.
     */
    if (props?.isLoading && modalData?.selectedColorIndex !== null) {
      modalData?.setSelectedColorIndex(null, false);
      loaded.current = false;
    }
  }, [props?.isLoading, modalData?.selectedSizeIdx]);

  return (
    <QVMProvider
      value={{
        ...modalData,
        ...actions,
        ...props,
        onAddToBag,
        selectedVariant,
        selectedColor,
        skus,
      }}
    >
      <ModalRenderer>
        <ErrorBoundary
          onError={(e) => {
            onError?.(e);
            props?.onClose?.();
          }}
        >
          <CarouselRenderer />
          <ContentRenderer />
        </ErrorBoundary>
      </ModalRenderer>
    </QVMProvider>
  );
};

QuickViewModal.propTypes = propTypes;

export default QuickViewModal;
